import type { LetterSounds } from '../types';
import type { SayStep } from '../hooks/useSay';
import { ALPHABET } from './alphabet';

/* Catálogo de frases fijas que dicen los juegos ("¡Muy bien!", "¡Ganaste!"…),
   para poder grabarlas en el estudio (Admin → Grabaciones) igual que una
   palabra. Las grabaciones viven en el mismo almacén clave → audio que las
   letras (`letterSounds`), con la clave de `phraseKey`.

   Una frase que cambia según la palabra ("¡Muy bien! ¡Perro!") no se graba
   entera: se arma encadenando la frase fija y la grabación de la palabra con
   `sayEach` (ver `phraseStep`). */

export type Phrase = {
  id: string;
  es: string;
  /** Versión en inglés, para los juegos con el toggle de idioma. */
  en?: string;
  /** Dónde suena, para quien graba. */
  hint?: string;
};

export const PHRASES: Phrase[] = [
  { id: 'muy_bien', es: '¡Muy bien!', en: 'Great!', hint: 'Festejo al acertar. Suena sola o antes de la palabra.' },
  { id: 'ganaste', es: '¡Ganaste!', en: 'You won!', hint: 'Al terminar Memotest y Memo Fácil.' },
  {
    id: 'llamar',
    es: '¡Ahora tocá el botón verde para llamar!',
    en: 'Now tap the green button to call!',
    hint: 'Teléfono, después de marcar.',
  },
  { id: 'llamando_a', es: 'Llamando a…', en: 'Calling…', hint: 'Teléfono, al llamar. Después suena el nombre.' },
  { id: 'contemos', es: '¡Contemos!', en: 'Let us count!', hint: 'Contar, al empezar cada ronda y al equivocarse.' },
  { id: 'cuantos_hay', es: '¿Cuántos hay?', en: 'How many are there?', hint: 'Contar, cuando ya contó todo y tiene que elegir el número.' },
  { id: 'hay', es: 'Hay…', en: 'There are…', hint: 'Contar, al acertar. Después suenan el número y el objeto: "Hay… tres… manzanas".' },
  /* En inglés es una sola para los dos géneros ("There is one…"): `hay_una` no lleva `en`. */
  { id: 'hay_un', es: 'Hay un…', en: 'There is one…', hint: 'Contar, al acertar con uno solo de un objeto masculino. Después suena el objeto: "Hay un… gato".' },
  { id: 'hay_una', es: 'Hay una…', hint: 'Contar, al acertar con una sola de un objeto femenino. Después suena el objeto: "Hay una… manzana".' },

  /* Pedazos de frases que siguen con una palabra, una letra o una sílaba
     (ver `composeSteps`). Los "…" marcan que después suena otra cosa: se
     graban con la entonación en suspenso, sin cerrar la frase. */
  { id: 'tren_armemos', es: '¡Armemos el tren de…', hint: 'Tren de Sílabas. Después suena la palabra.' },
  { id: 'tren_partes', es: '¡Busquemos las partes para armar su tren!', hint: 'Tren de Sílabas, después de la palabra.' },
  { id: 'esa_es', es: 'Esa es…', hint: 'Tren de Sílabas, al tocar la sílaba equivocada. Después suena la sílaba.' },
  { id: 'busquemos', es: 'Busquemos…', hint: 'Tren de Sílabas. Después suena la sílaba que falta.' },
  { id: 'empiezan_con_letra', es: '¿Qué cosas empiezan con la letra…', hint: 'Sonido Inicial. Después suena la letra.' },
  { id: 'empieza_con', es: '¡Empieza con…', hint: 'Sonido Inicial, después de la palabra. Después suena la letra.' },
  { id: 'encontraste_todo', es: '¡Muy bien! ¡Encontraste todo lo que empieza con…', hint: 'Sonido Inicial, al terminar. Después suena la letra.' },
  { id: 'busquemos_con', es: 'Busquemos cosas con…', hint: 'Sonido Inicial, al equivocarse. Después suena la letra.' },
  { id: 'globos_letra', es: '¡Explotá todos los globos con la letra…', hint: 'Cazador de Letras. Después suena la letra.' },
  { id: 'busca_letra', es: '¡Buscá la letra…', hint: 'Cazador de Letras, botón de ayuda. Después la letra y su sonido.' },
  { id: 'explota_globos', es: '¡Explotá todos sus globos!', hint: 'Cazador de Letras, cierra la ayuda.' },
  { id: 'explotaste_todas', es: '¡Muy bien! ¡Explotaste todas las letras…', hint: 'Cazador de Letras, al terminar. Después suena la letra.' },
  { id: 'esa_es_la', es: 'Esa es la…', hint: 'Cazador de Letras, al equivocarse. Después suena la letra.' },
  { id: 'explota_la', es: '¡Explotá la…', hint: 'Cazador de Letras, al equivocarse. Después suena la letra.' },
  { id: 'mente_correcto', es: '¡Muy bien! ¡Eso es lo que pensaba!', en: 'Great! That is what they thought!', hint: 'Preguntas de mente: acierto basado en creencia del personaje.' },
  { id: 'mente_repaso', es: 'Vamos a ver qué pasó antes…', en: 'Let us see what happened before…', hint: 'Preguntas de mente: repaso pedagógico suave.' },
];

/* Las grabaciones, también para lo que no es un componente (el "¡Muy bien!"
   de `celebrate()` en lib/confetti.ts). Lo carga el DataProvider. */
let registry: LetterSounds = {};

export function registerClips(clips: LetterSounds): void {
  registry = clips;
}

export function getClip(key: string): string | undefined {
  return registry[key] || undefined;
}

export function phraseKey(id: string, lang: 'es' | 'en' = 'es'): string {
  return lang === 'en' ? `frase:${id}:en` : `frase:${id}`;
}

/** Clave de `letterSounds` de una sílaba: mayúscula y sin tilde (PÁ → PA),
 *  pero conservando la Ñ, que `normalize()` convertiría en N. */
export function syllableKey(syl: string): string {
  return syl.toUpperCase().replace(/[ÁÉÍÓÚÜ]/g, (c) => 'AEIOUU'['ÁÉÍÓÚÜ'.indexOf(c)]);
}

/** Clave de la grabación del ancla de una letra ("B de Barco"). */
export function anchorKey(upper: string): string {
  return `ancla:${upper}`;
}

/** Clave de la grabación entera de una oración del juego Oraciones. */
export function oracionKey(id: string): string {
  return `oracion:${id}`;
}

/** Clave de una palabra que no está en Palabras pero se lee sola: los
 *  conectores de Oraciones ("el", "come"), las palabras de un cuento que se
 *  tocan, el ejemplo de una sílaba. Minúscula y con tildes: sin `normalize()`,
 *  que le saca la tilde a la Ñ y juntaría "año" con "ano". En inglés (Contar)
 *  lleva `:en` al final. */
export function looseWordKey(word: string, lang: 'es' | 'en' = 'es'): string {
  return lang === 'en'
    ? `palabra:${word.trim().toLocaleLowerCase('en')}:en`
    : `palabra:${word.trim().toLocaleLowerCase('es')}`;
}

/** Clave del título de un cuento, historia o cómic (se lee en la portada). */
export function titleKey(title: string): string {
  return `titulo:${title.trim().toLocaleLowerCase('es')}`;
}

/** Clave de un número (Calculadora, Teléfono). En inglés, `3:en`. */
export function numberKey(n: number | string, lang: 'es' | 'en' = 'es'): string {
  return lang === 'en' ? `${n}:en` : String(n);
}

/** Del 0 al 20, como se dicen: es lo que lee el TTS si el número no tiene grabación. */
const NUMBER_WORDS = [
  'cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
  'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho',
  'diecinueve', 'veinte',
];
const NUMBER_WORDS_EN = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

/** El paso de `useSay` que lee un número (Contar): del 0 al 20 en español, del 0 al 10 en inglés,
 *  con su grabación si está. */
export function numberStep(n: number, clips: LetterSounds, english = false): SayStep {
  if (english) return { text: NUMBER_WORDS_EN[n] ?? String(n), src: clips[numberKey(n, 'en')] || undefined, lang: 'en-US' };
  return { text: NUMBER_WORDS[n] ?? String(n), src: clips[numberKey(n)] || undefined };
}

/** El paso de `useSay` que lee una frase: su grabación si existe, TTS si no. */
export function phraseStep(id: string, clips: LetterSounds, english = false): SayStep {
  const phrase = PHRASES.find((p) => p.id === id);
  const en = english && Boolean(phrase?.en);
  return {
    text: (en ? phrase?.en : phrase?.es) ?? id,
    src: clips[phraseKey(id, en ? 'en' : 'es')],
    lang: en ? 'en-US' : undefined,
  };
}

/** Una frase del catálogo (su id) o un paso ya armado (palabra, letra…). */
export type Piece = string | SayStep;

/**
 * Una frase que cambia según la palabra o la letra, armada con pedazos
 * grabables: "¿Qué cosas empiezan con la letra…" + "eme". Para `sayEach`.
 *
 * Si algún pedazo está grabado, va cada uno en su paso (con su grabación o
 * TTS). Si ninguno, una sola lectura TTS con los textos unidos —más `end`, la
 * puntuación del final—, que suena más natural que varias cortadas.
 */
export function composeSteps(
  clips: LetterSounds,
  pieces: Piece[],
  opts: { key?: string; end?: string; english?: boolean } = {},
): SayStep[] {
  const steps = pieces.map((p) => (typeof p === 'string' ? phraseStep(p, clips, opts.english) : p));
  if (steps.some((s) => s.src)) {
    return steps.map((s) => ({ ...s, key: s.key ?? opts.key }));
  }
  const text = steps.map((s) => s.text.replace(/…$/, '')).join(' ') + (opts.end ?? '');
  return [{ key: opts.key, text, lang: steps[0]?.lang }];
}

/** "¡Muy bien! ¡Perro!": el festejo y la palabra (ver `composeSteps`). */
export function cheerSteps(clips: LetterSounds, word: SayStep, english = false): SayStep[] {
  return composeSteps(
    clips,
    ['muy_bien', { ...word, text: english ? word.text : `¡${word.text}` }],
    { key: word.key ?? undefined, end: '!', english },
  );
}

/** El nombre de una letra ("eme"), con su grabación si está. En inglés
 *  ("em"), la clave lleva `:en` y el TTS lee la letra sola. */
export function letterNameStep(upper: string, clips: LetterSounds, english = false): SayStep {
  const U = upper.toUpperCase();
  if (english) return { text: U, src: clips[`${U}_nombre:en`], lang: 'en-US' };
  const l = ALPHABET.find((x) => x.upper === U);
  return { text: l?.name ?? upper.toLowerCase(), src: clips[`${U}_nombre`] };
}

/** El sonido de una letra ("mmm"), con su grabación si está. */
export function letterSoundStep(upper: string, clips: LetterSounds): SayStep {
  const l = ALPHABET.find((x) => x.upper === upper.toUpperCase());
  return { text: l?.sound ?? upper.toLowerCase(), src: clips[`${upper.toUpperCase()}_sonido`] };
}

/** Una sílaba ("ta"), con su grabación si está (solo las directas: BA, ÑO…). */
export function syllableStep(syl: string, clips: LetterSounds): SayStep {
  return { text: syl.toLowerCase(), src: clips[syllableKey(syl)] };
}
